import { useEffect, useMemo, useState } from 'react';
import { Modal, TextInput, Textarea, NumberInput, Stack, Group, Button, Alert, Paper, Table, Text, Title, ActionIcon, Loader, Center, Anchor } from '@mantine/core';
import { IconAdd as IconPlus } from '../../components/icons/CncIcons';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import ImageUpload from '../../components/ImageUpload';
import MarkupSlider from '../../components/MarkupSlider';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import DistributorModal, { type Distributor } from './DistributorModal';

interface ConsumableFull {
  id: number;
  name: string;
  notes: string | null;
  imagePath: string | null;
  typicalCostEur: number;
  markupPct: number;
  typicalPriceEur: number | null;
  distributors: Distributor[];
}

export default function ConsumableModal({
  id,
  onClose,
  onSaved,
  onDeleted,
}: {
  id: number | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const [loading, setLoading] = useState(!!id);
  const [data, setData] = useState<ConsumableFull | null>(null);

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [image, setImage] = useState<string | null>(null);
  const [cost, setCost] = useState<number | string>(0);
  const [markup, setMarkup] = useState(30);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [editingDist, setEditingDist] = useState<Distributor | 'new' | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const full = await api.get<ConsumableFull>(`/consumables/${id}`);
        setData(full);
        setName(full.name);
        setNotes(full.notes ?? '');
        setImage(full.imagePath);
        setCost(full.typicalCostEur);
        setMarkup(full.markupPct);
        setDistributors(full.distributors);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const typicalPrice = useMemo(() => {
    const c = Number(cost);
    if (Number.isNaN(c)) return null;
    return c * (1 + markup / 100);
  }, [cost, markup]);

  const dirty =
    !id ? (name.length > 0 || notes.length > 0 || image !== null || Number(cost) !== 0 || markup !== 30)
        : (name !== (data?.name ?? '')
          || notes !== (data?.notes ?? '')
          || image !== (data?.imagePath ?? null)
          || Number(cost) !== (data?.typicalCostEur ?? 0)
          || markup !== (data?.markupPct ?? 30));

  function confirmClose() {
    if (!dirty) return onClose();
    modals.openConfirmModal({
      title: 'Discard changes?',
      children: 'You will lose your edits.',
      labels: { confirm: 'Discard', cancel: 'Keep editing' },
      confirmProps: { color: 'red' },
      onConfirm: onClose,
    });
  }

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Name is required.');
    if (Number(cost) < 0) return setErr('Cost cannot be negative.');
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        notes: notes || null,
        imagePath: image,
        typicalCostEur: Number(cost),
        markupPct: markup,
      };
      if (id) await api.put(`/consumables/${id}`, body);
      else await api.post('/consumables', body);
      notifications.show({ message: 'Consumable saved.', color: 'green' });
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!id) return;
    modals.openConfirmModal({
      title: `Delete ${data?.name}?`,
      children: 'This cannot be undone.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/consumables/${id}`);
          notifications.show({ message: 'Consumable deleted.', color: 'gray' });
          onDeleted();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  async function refreshDistributors() {
    if (!id) return;
    const full = await api.get<ConsumableFull>(`/consumables/${id}`);
    setDistributors(full.distributors);
  }

  return (
    <Modal opened onClose={confirmClose} title={id ? `Edit ${data?.name ?? 'Consumable'}` : 'Add Consumable'} size="xl">
      {loading ? (
        <Center p="xl"><Loader /></Center>
      ) : (
        <Stack>
          <ImageUpload entity="consumables" value={image} onChange={setImage} />
          <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required maxLength={120} disabled={!writable} data-tour="consumable-name" />
          <Textarea
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            maxLength={500}
            autosize
            minRows={3}
            description={`${notes.length}/500`}
            disabled={!writable}
          />
          <Group grow align="flex-start">
            <NumberInput
              data-tour="consumable-cost"
              label="Typical Cost (EUR/unit)"
              value={cost}
              onChange={setCost}
              min={0}
              step={0.5}
              decimalScale={2}
              disabled={!writable}
            />
            <Stack gap={4}>
              <Text size="sm" fw={500}>Typical Price (computed)</Text>
              <Paper withBorder p="xs"><Text fw={600}>{typicalPrice != null ? `€ ${typicalPrice.toFixed(2)}` : '—'}</Text></Paper>
            </Stack>
          </Group>
          <MarkupSlider value={markup} onChange={setMarkup} disabled={!writable} />

          <Title order={5} mt="md">Distributors</Title>
          {!id ? (
            <Text c="dimmed" size="sm" className="section-stub">
              Save the consumable first; then you can add distributors.
            </Text>
          ) : (
            <Paper withBorder>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Link</Table.Th>
                    <Table.Th>Contact</Table.Th>
                    <Table.Th>Price</Table.Th>
                    <Table.Th>Added</Table.Th>
                    <Table.Th>By</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {distributors.map((d) => (
                    <Table.Tr key={d.id} className="click-row" onClick={() => setEditingDist(d)}>
                      <Table.Td>{d.name}</Table.Td>
                      <Table.Td>{d.link ? <Anchor href={d.link} target="_blank" onClick={(e) => e.stopPropagation()}>Open</Anchor> : '—'}</Table.Td>
                      <Table.Td>{d.contact ?? '—'}</Table.Td>
                      <Table.Td>{d.priceEur != null ? `€ ${d.priceEur.toFixed(2)}` : '—'}</Table.Td>
                      <Table.Td>{new Date(d.addedAt).toLocaleString()}</Table.Td>
                      <Table.Td>{d.addedByUsername ?? '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setEditingDist('new')} disabled={!writable}>
                        Add Distributor
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {err && <Alert color="red">{err}</Alert>}
          <Group justify="space-between" mt="md">
            <div>
              {id && writable && <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>}
            </div>
            <Group>
              <Button variant="default" onClick={confirmClose}>Close</Button>
              {writable && <Button onClick={save} loading={busy} data-tour="consumable-save">Save</Button>}
            </Group>
          </Group>

          {id && editingDist !== null && (
            <DistributorModal
              consumableId={id}
              initial={editingDist === 'new' ? null : editingDist}
              onClose={() => setEditingDist(null)}
              onDone={async () => { await refreshDistributors(); setEditingDist(null); }}
            />
          )}
        </Stack>
      )}
    </Modal>
  );
}
